import { useEffect, useState } from 'react';
import { dataResidencyManager } from './DataResidencyManager';

function readAvailability(): boolean {
  // Asked by backend region rather than by residency so the canonical
  // home country -> residency -> region derivation stays the single source of
  // truth, and a future country or region flows through without a change here.
  return dataResidencyManager.getBackendRegion() !== 'eu-central-1';
}

/**
 * Whether anything that signs the user in with Google, Clever or ClassLink may
 * be offered. That covers SSO log-in and sign-up (PE-1784) and classroom roster
 * sync/import (PE-1785), which is gated by the same fact rather than by a
 * separate one: importing a roster signs the teacher in with the provider
 * first, so it inherits the same constraint.
 *
 * Only the us-east-1 backend has a usable Cognito hosted UI. The eu-central-1
 * pools have OAuth disabled on the app client outright - no flows, no callback
 * or logout URLs, no identity providers - so signing in through those providers
 * would conflict with the FRA Cognito pools, and offering any of it is a dead
 * end.
 *
 * Re-reads on residency change, so switching country in the home-country
 * control updates the UI without a reload. The first paint uses whatever the
 * manager knows synchronously (an explicit choice or stored residency), so a
 * first-time visitor still being geo-detected briefly sees the buttons before
 * they disappear; that is accepted, since us-east-1 is the default and the
 * common case.
 */
export function useIsFederatedSignInAvailable(): boolean {
  const [isAvailable, setIsAvailable] = useState(readAvailability);

  useEffect(() => {
    // Re-read on mount too: the residency can resolve between this component's
    // first render and the subscription being installed.
    setIsAvailable(readAvailability());
    return dataResidencyManager.subscribeToChanges(() => setIsAvailable(readAvailability()));
  }, []);

  return isAvailable;
}
